import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import { MapContainer, Marker, Popup, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import { useHasPageAccess } from "../../masterLayout/MasterLayoutBody";
import { getOnlineUsers } from "../../api/onlineUsersAPI";
import {
  getLoginMetricsSummary,
  getLoginMetricsTimeSeries,
  getSuccessfulLoginUsers,
} from "../../api/loginMetricsAPI";
import { formatIntDateTime } from '../../utils/dateUtils';

const defaultCenter = [20, 0];
const defaultZoom = 2;

L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const MapBounds = ({ points }) => {
  const map = useMap();

  useEffect(() => {
    if (points.length === 0) {
      map.setView(defaultCenter, defaultZoom);
      return;
    }
    const bounds = L.latLngBounds(
      points.map((point) => [point.latitude, point.longitude]),
    );
    map.fitBounds(bounds, { padding: [32, 32], maxZoom: 6 });
  }, [map, points]);

  return null;
};

const ApplicationDashboardLayer = () => {
  const hasPageAccess = useHasPageAccess();
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [usersByInterval, setUsersByInterval] = useState([]);
  const [userCounts, setUserCounts] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [loginSummary, setLoginSummary] = useState(null);
  const [loginSeries, setLoginSeries] = useState([]);
  const [loginError, setLoginError] = useState("");
  const [loginLoading, setLoginLoading] = useState(true);
  const [successfulLoginUsers, setSuccessfulLoginUsers] = useState([]);
  const canAccess = hasPageAccess('/dashboards/application');

  useEffect(() => {
    if (!canAccess) {
      return;
    }

    let isMounted = true;

    const fetchOnlineUsers = async () => {
      try {
        const data = await getOnlineUsers();
        if (!isMounted) {
          return;
        }
        const totalOnline = Number.isFinite(data?.totalOnline)
          ? data.totalOnline
          : 0;
        const timestamp = new Date().toISOString();
        setOnlineUsers(Array.isArray(data?.users) ? data.users : []);
        setUsersByInterval(
          Array.isArray(data?.usersByInterval) ? data.usersByInterval : [],
        );
        setUserCounts((prev) => {
          const next = [...prev, { x: timestamp, y: totalOnline }];
          return next.slice(-240);
        });
        setErrorMessage("");
      } catch (error) {
        if (!isMounted) {
          return;
        }
        setErrorMessage("Unable to load online user data.");
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchOnlineUsers();
    const intervalId = setInterval(fetchOnlineUsers, 15000);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [canAccess]);

  useEffect(() => {
    if (!canAccess) {
      return;
    }

    let isMounted = true;

    const fetchLoginMetrics = async () => {
      try {
        const [summary, series, successfulUsers] = await Promise.all([
          getLoginMetricsSummary(),
          getLoginMetricsTimeSeries(),
          getSuccessfulLoginUsers(),
        ]);
        if (!isMounted) {
          return;
        }
        setLoginSummary(summary);
        setLoginSeries(Array.isArray(series?.series) ? series.series : []);
        setSuccessfulLoginUsers(Array.isArray(successfulUsers) ? successfulUsers : []);
        setLoginError("");
      } catch (error) {
        if (!isMounted) {
          return;
        }
        setLoginError("Unable to load login activity metrics.");
      } finally {
        if (isMounted) {
          setLoginLoading(false);
        }
      }
    };

    fetchLoginMetrics();

    return () => {
      isMounted = false;
    };
  }, [canAccess]);

  const chartOptions = useMemo(
    () => ({
      chart: {
        type: "line",
        height: 280,
        toolbar: { show: false },
        animations: { enabled: false },
      },
      stroke: {
        curve: "stepline",
        width: 4,
      },
      markers: {
        size: 2,
      },
      xaxis: {
        type: "datetime",
        labels: {
          datetimeUTC: false,
        },
      },
      yaxis: {
        title: {
          text: "Users online",
        },
        min: 0,
        forceNiceScale: true,
      },
      tooltip: {
        x: {
          format: "HH:mm:ss",
        },
      },
      grid: {
        strokeDashArray: 4,
      },
    }),
    [],
  );

  const chartSeries = useMemo(
    () => [
      {
        name: "Online users",
        data: userCounts,
      },
    ],
    [userCounts],
  );

  const intervalCategories = useMemo(
    () => usersByInterval.map((entry) => entry.interval),
    [usersByInterval],
  );

  const intervalSeries = useMemo(
    () => [
      {
        name: "Users",
        data: usersByInterval.map((entry) =>
          Number.isFinite(Number(entry.users)) ? Number(entry.users) : 0,
        ),
      },
    ],
    [usersByInterval],
  );

  const intervalChartOptions = useMemo(
    () => ({
      chart: {
        type: "bar",
        height: 280,
        toolbar: { show: false },
      },
      plotOptions: {
        bar: {
          horizontal: true,
          borderRadius: 6,
        },
      },
      dataLabels: {
        enabled: false,
      },
      xaxis: {
        categories: intervalCategories,
        title: {
          text: "# Users",
        },
      },
      yaxis: {
        min: 0,
        forceNiceScale: true,
      },
      grid: {
        strokeDashArray: 4,
      },
    }),
    [intervalCategories],
  );

  const companyCounts = useMemo(() => {
    return onlineUsers.reduce((acc, user) => {
      const company = user.tenant || "Unassigned";
      acc[company] = (acc[company] || 0) + 1;
      return acc;
    }, {});
  }, [onlineUsers]);

  const companyLabels = useMemo(
    () => Object.keys(companyCounts),
    [companyCounts],
  );

  const companySeries = useMemo(
    () => Object.values(companyCounts),
    [companyCounts],
  );

  const companyChartOptions = useMemo(
    () => ({
      chart: {
        type: "donut",
        height: 280,
      },
      plotOptions: {
        pie: {
          donut: {
            size: "65%",
          },
        },
      },
      labels: companyLabels,
      legend: {
        position: "bottom",
      },
      dataLabels: {
        enabled: true,
      },
    }),
    [companyLabels],
  );

  const latestCount = userCounts.length
    ? userCounts[userCounts.length - 1].y
    : 0;

  const minutesSince = (lastSeen) => {
    const last = new Date(lastSeen).getTime();
    const now = Date.now();
    return Math.floor((now - last) / 60000);
  };

  const presenceColour = (minutes) => {
    if (minutes <= 15) return 'bg-success';
    if (minutes <= 25) return 'bg-warning';
    return 'bg-danger';
  };

  const totalAttempts = Number.isFinite(loginSummary?.totalAttempts)
    ? loginSummary.totalAttempts
    : 0;
  const failureCount = Number.isFinite(loginSummary?.failureCount)
    ? loginSummary.failureCount
    : 0;
  const successRate = Number.isFinite(loginSummary?.successRate)
    ? loginSummary.successRate
    : 0;
  const uniqueSuccessUsers = Number.isFinite(loginSummary?.uniqueSuccessUsers)
    ? loginSummary.uniqueSuccessUsers
    : 0;

  const successRateClass = successRate >= 90
    ? 'text-success'
    : successRate >= 75
      ? 'text-warning'
      : 'text-danger';

  const failureCountClass = failureCount >= 20
    ? 'text-danger'
    : failureCount >= 10
      ? 'text-warning'
      : 'text-neutral-500';

  const loginSeriesData = useMemo(
    () => (Array.isArray(loginSeries) ? loginSeries : []),
    [loginSeries],
  );

  const loginChartCategories = useMemo(
    () =>
      loginSeriesData.map((point) =>
        typeof point.hour === 'string' ? point.hour.slice(11, 16) : '',
      ),
    [loginSeriesData],
  );

  const loginChartOptions = useMemo(
    () => ({
      chart: {
        type: "bar",
        height: 280,
        toolbar: { show: false },
      },
      colors: ["#198754", "#dc3545"],
      dataLabels: {
        enabled: false,
      },
      plotOptions: {
        bar: {
          columnWidth: "60%",
          borderRadius: 6,
        },
      },
      stroke: {
        show: true,
        width: 2,
        colors: ["#ffffff"],
      },
      xaxis: {
        categories: loginChartCategories,
        title: {
          text: "Hour (UTC)",
        },
      },
      yaxis: {
        min: 0,
        forceNiceScale: true,
      },
      grid: {
        strokeDashArray: 4,
      },
      legend: {
        position: "top",
      },
    }),
    [loginChartCategories],
  );

  const loginChartSeries = useMemo(
    () => [
      {
        name: "Success",
        data: loginSeriesData.map((point) =>
          Number.isFinite(Number(point.successCount))
            ? Number(point.successCount)
            : 0,
        ),
      },
      {
        name: "Failure",
        data: loginSeriesData.map((point) =>
          Number.isFinite(Number(point.failureCount))
            ? Number(point.failureCount)
            : 0,
        ),
      },
    ],
    [loginSeriesData],
  );

  const failedLoginRecords = Array.isArray(loginSummary?.failedLoginRecords)
    ? loginSummary.failedLoginRecords
    : [];

  const onlineUserLocations = useMemo(
    () =>
      onlineUsers
        .map((user) => ({
          ...user,
          latitude: Number(user?.location?.latitude),
          longitude: Number(user?.location?.longitude),
          city: user?.location?.city || "Unknown",
        }))
        .filter(
          (user) =>
            Number.isFinite(user.latitude) && Number.isFinite(user.longitude),
        ),
    [onlineUsers],
  );

  if (!canAccess) {
    return <Navigate to="/access-denied" replace />;
  }

  return (
    <div className="row gy-4 p-24">
      <div className="col-12">
        <div className="card h-100">
          <div className="card-body">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
              <div>
                <h5 className="card-title mb-1">Login Activity (24h)</h5>
              </div>
            </div>
            {loginError ? (
              <div className="alert alert-warning mb-3" role="alert">
                {loginError}
              </div>
            ) : null}
            <div className="row g-3">
              <div className="col-12 col-sm-6 col-xl-3">
                <div className="border rounded p-3 h-100">
                  <span className="text-neutral-500 d-block">Logins (24h)</span>
                  <span className="h6 mb-0">{totalAttempts}</span>
                </div>
              </div>
              <div className="col-12 col-sm-6 col-xl-3">
                <div className="border rounded p-3 h-100">
                  <span className="text-neutral-500 d-block">
                    Failed logins (24h)
                  </span>
                  <span className={`h6 mb-0 ${failureCountClass}`}>
                    {failureCount}
                  </span>
                </div>
              </div>
              <div className="col-12 col-sm-6 col-xl-3">
                <div className="border rounded p-3 h-100">
                  <span className="text-neutral-500 d-block">
                    Success rate
                  </span>
                  <span className={`h6 mb-0 ${successRateClass}`}>
                    {successRate}%
                  </span>
                </div>
              </div>
              <div className="col-12 col-sm-6 col-xl-3">
                <div className="border rounded p-3 h-100">
                  <span className="text-neutral-500 d-block">
                    Unique users (24h)
                  </span>
                  <span className="h6 mb-0 text-success">
                    {uniqueSuccessUsers}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
            <div className="col-12 col-lg-6">
        <div className="card h-100">
          <div className="card-body">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
              <h5 className="card-title mb-0">Hourly Login Activity</h5>
              <span className="text-neutral-500">Last 24 hours (UTC)</span>
            </div>
            {loginLoading ? (
              <div className="text-center text-neutral-500 py-5">
                Loading login activity...
              </div>
            ) : (
              <ReactApexChart
                options={loginChartOptions}
                series={loginChartSeries}
                type="bar"
                height={280}
              />
            )}
          </div>
        </div>
      </div>
      <div className="col-12 col-lg-6">
        <div className="card h-100">
          <div className="card-body">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
              <div>
                <h5 className="card-title mb-1">Online Users</h5>
              </div>
              <div className="text-end">
                <span className="text-neutral-500 d-block">Current total</span>
                <span className="h4 mb-0">{latestCount}</span>
              </div>
            </div>
            {errorMessage ? (
              <div className="alert alert-warning mb-3" role="alert">
                {errorMessage}
              </div>
            ) : null}
            <ReactApexChart
              options={chartOptions}
              series={chartSeries}
              type="line"
              height={200}
            />
          </div>
        </div>
      </div>
      <div className="col-12 col-lg-6">
        <div className="card h-100">
          <div className="card-body">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
              <h5 className="card-title mb-0">Online Users Map</h5>
              <span className="text-neutral-500">
                Showing {onlineUserLocations.length} users with location data
              </span>
            </div>
            {onlineUserLocations.length === 0 ? (
              <div className="text-center text-neutral-500 py-5">
                No geolocation data available for online users yet.
              </div>
            ) : (
              <div style={{ height: 360, width: "100%" }}>
                <MapContainer
                  center={defaultCenter}
                  zoom={defaultZoom}
                  scrollWheelZoom={false}
                  style={{ height: "100%", width: "100%" }}
                >
                  <TileLayer
                    attribution="&copy; OpenStreetMap contributors"
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <MapBounds points={onlineUserLocations} />
                  {onlineUserLocations.map((user, index) => (
                    <Marker
                      key={`${user.email}-${user.ipAddress ?? "unknown"}-${index}`}
                      position={[user.latitude, user.longitude]}
                    >
                      <Popup>
                        <div className="fw-semibold">{user.email}</div>
                        <div className="text-neutral-500 small">
                          {user.city}
                        </div>
                        <div className="text-neutral-500 small">
                          {user.company || "Unknown"} · {user.tenant || "Unassigned"}
                        </div>
                        <div className="text-neutral-500 small">
                          IP: {user.ipAddress || "Unknown"}
                        </div>
                        <div className="text-neutral-500 small">
                          Last seen: {formatIntDateTime(user.lastSeen) || "Unknown"}
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="col-12 col-lg-3">
        <div className="card h-100">
          <div className="card-body">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
              <h5 className="card-title mb-0">Users by Company</h5>
              <span className="text-neutral-500">
                Total: {onlineUsers.length}
              </span>
            </div>
            {companySeries.length === 0 ? (
              <div className="text-center text-neutral-500 py-5">
                No company data available.
              </div>
            ) : (
              <ReactApexChart
                options={companyChartOptions}
                series={companySeries}
                type="donut"
                height={280}
              />
            )}
          </div>
        </div>
      </div>
      <div className="col-12 col-lg-3">
        <div className="card h-100">
          <div className="card-body">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
              <h5 className="card-title mb-0">Users by Interval</h5>
              <span className="text-neutral-500">
                Latest interval snapshot
              </span>
            </div>
            <ReactApexChart
              options={intervalChartOptions}
              series={intervalSeries}
              type="bar"
              height={280}
            />
          </div>
        </div>
      </div>
      <div className="col-12 col-lg-4">
        <div className="card h-100">
          <div className="card-body">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
              <h5 className="card-title mb-0">Failed Logins</h5>
              <span className="text-neutral-500">Last 48 hours</span>
            </div>
            <div className="table-responsive" style={{height: "350px", overflowY: "auto" }}>
              <table className="table bordered-table align-middle">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>IP Address</th>
                    <th>Failed at</th>
                  </tr>
                </thead>
                <tbody>
                  {loginLoading ? (
                    <tr>
                      <td colSpan="3" className="text-center text-neutral-500">
                        Loading failed login records...
                      </td>
                    </tr>
                  ) : failedLoginRecords.length === 0 ? (
                    <tr>
                      <td colSpan="3" className="text-center text-neutral-500">
                        No failed logins in the last 48 hours.
                      </td>
                    </tr>
                  ) : (
                    failedLoginRecords.map((user, index) => (
                      <tr key={`${user.email}-${index}`}>
                        <td>
                          <div className="fw-semibold">{user.email}</div>
                          <div className="text-neutral-500 small">
                            {user.fullName || "Unknown"}
                          </div>
                        </td>
                        <td>{user.ipAddress || "Unknown"}</td>
                        <td>{formatIntDateTime(user.failedAt) || "Unknown"}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <div className="col-12 col-lg-8">
        <div className="card h-100">
          <div className="card-body">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
              <h5 className="card-title mb-0">Current Online Users</h5>
              <span className="text-neutral-500">
                Total users: {onlineUsers.length}
              </span>
            </div>
            <div className="table-responsive" style={{height: "350px", overflowY: "auto" }}>
              <table className="table bordered-table align-middle">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Company</th>
                    <th>Tenant</th>
                    <th>IP Address</th>
                    <th>Last Seen</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan="4" className="text-center text-neutral-500">
                        Loading online users...
                      </td>
                    </tr>
                  ) : onlineUsers.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="text-center text-neutral-500">
                        No users are currently online.
                      </td>
                    </tr>
                  ) : (
                    onlineUsers.map((user, index) => (
                      <tr
                        key={`${user.email}-${user.ipAddress ?? "unknown"}-${index}`}
                      >
                        <td>
                          <span
                            className={`rounded-circle ${presenceColour(minutesSince(user.lastSeen))} me-8`}
                            style={{ width: '8px', height: '8px', display: 'inline-block' }}
                            title={`Last seen ${minutesSince(user.lastSeen)} min ago`}
                          />
                          {user.email}
                        </td>
                        <td>{user.company || "Unknown"}</td>
                        <td>{user.tenant || "Unassigned"}</td>
                        <td>{user.ipAddress || "Unknown"}</td>
                        <td>{formatIntDateTime(user.lastSeen) || "Unknown"}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <div className="col-12">
        <div className="card h-100">
          <div className="card-body">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
              <h5 className="card-title mb-0">Successful User Logins</h5>
              <span className="text-neutral-500">Past 30 days (grouped by email)</span>
            </div>
            <div className="table-responsive" style={{height: "350px", overflowY: "auto" }}>
              <table className="table bordered-table align-middle">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Company</th>
                    <th>Tenant</th>
                    <th>Latest IP Address</th>
                    <th>Latest Created At</th>
                    <th>Total Logins</th>
                  </tr>
                </thead>
                <tbody>
                  {loginLoading ? (
                    <tr>
                      <td colSpan="6" className="text-center text-neutral-500">
                        Loading successful login users...
                      </td>
                    </tr>
                  ) : successfulLoginUsers.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center text-neutral-500">
                        No successful logins in the past 30 days.
                      </td>
                    </tr>
                  ) : (
                    successfulLoginUsers.map((user, index) => (
                      <tr key={`${user.email}-${index}`}>
                        <td>{user.email || "Unknown"}</td>
                        <td>{user.company || "Unknown"}</td>
                        <td>{user.tenant || "Unassigned"}</td>
                        <td>{user.latestIpAddress || "Unknown"}</td>
                        <td>{formatIntDateTime(user.latestCreatedAt) || "Unknown"}</td>
                        <td>{user.loginCount ?? 0}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplicationDashboardLayer;
